<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import userStore from '@/store/UserStore'
import ButtonSet from '@/components/ButtonSet.vue'
import FormElement from '@/components/FormElement.vue'
import formStore from '@/store/FormStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import { IsolationType } from '@/helper/Types'
import CalculationResultBadge from '@/components/steps/planning/CalculationResultBadge.vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ComboBox from '@/components/ComboBox.vue'
import i18n from '@/helper/Translations'
import { computed } from 'vue'

const props = defineProps<{
  validator: ValidatorType
  calculatedHeatingLoad: string
}>()

defineEmits<{
  (e: 'updateHeatingLoad'): void
}>()

defineExpose({
  validator: props.validator
})

const isolationType = computed(() => {
  return IsolationType
})

const windowTypes = computed(() => {
  return userStore.heatingWindowTypes.map(windowType => ({
    label: i18n.global.t(`step_heating_details.window_types.${windowType}`),
    value: windowType
  }))
})

const heatingIndoorTemperatures = computed(() => {
  return userStore.heatingIndoorTemperatures.map(temperature => ({
    label: `${temperature} ${i18n.global.t('global.degree_celsius')}`,
    value: temperature
  }))
})

const ventilationTypes = computed(() => {
  return userStore.heatingVentilationTypes.map(ventilationType => ({
    label: i18n.global.t(`step_heating_details.ventilation_types.${ventilationType}`),
    value: ventilationType
  }))
})
</script>

<template>
  <section class="form__section has--line section--heat-loss-calculation" data-hj-whitelist>
    <div class="form__section-inner has--line">
      <SectionHeadline class="form__title" translation-key="step_heating_details.title_heat_loss_calculation" />

      <div class="form__subsection">
        <h3 class="form__subtitle">
          {{ $t('step_heating_details.subtitle_building') }}
        </h3>

        <ButtonSet
          key="button-set-adjoint-sides"
          v-model="formStore.data.adjoinedBuildingSides"
          v-validate="projectStore.projectProperties.adjoinedBuildingSides.formValidationRule"
          :buttons="[
            {
              caption: $t('step_heating_details.adjoined_sides.DETACHED'),
              value: '0',
              iconIdentifier: 'ic-adjoined_none'
            },
            {
              caption: $t('step_heating_details.adjoined_sides.TERRACED_MIDDLE'),
              value: '2',
              iconIdentifier: 'ic-adjoined_two'
            },
            {
              caption: $t('step_heating_details.adjoined_sides.TERRACED_END'),
              value: '1',
              iconIdentifier: 'ic-adjoined_one'
            }
          ]"
          :error-text="validator.adjointSides?.error.value"
          :has-large-space="true"
          :large="true"
          :required="projectStore.formPropertyIsRequired('adjoinedBuildingSides')"
          :set-label-caption="$t('step_heating_details.label_adjoined_sides')"
          name="adjoint-sides"
          @change="$emit('updateHeatingLoad')"
        />

        <ButtonSet
          key="button-set-isolation"
          v-model="formStore.data.isolation"
          v-validate="projectStore.projectProperties.isolation.formValidationRule"
          :buttons="[
            {
              caption: $t('step_heating_details.improvements.WALLS'),
              value: isolationType.WALLS,
              iconIdentifier: 'ic-improved_wall'
            },
            {
              caption: $t('step_heating_details.improvements.FLOOR'),
              value: isolationType.FLOOR,
              iconIdentifier: 'ic-improved_basement'
            },
            {
              caption: $t('step_heating_details.improvements.ROOF'),
              value: isolationType.ROOF,
              iconIdentifier: 'ic-improved_roof'
            }
          ]"
          :error-text="validator.isolation?.error.value"
          :has-large-space="true"
          :is-unselectable="true"
          :large="true"
          :set-label-caption="$t('step_heating_details.label_building_improvements')"
          name="isolation"
          @change="$emit('updateHeatingLoad')"
        />
      </div>

      <div class="form__subsection">
        <h3 class="form__subtitle">
          {{ $t('step_heating_details.subtitle_windows') }}
        </h3>

        <div class="row form__row has--large-space">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.windowType.formValidationRule"
              :error="validator.windowType?.error"
              :required="projectStore.formPropertyIsRequired('windowType')"
              label-key="step_heating_details.placeholder_window_type"
            >
              <ComboBox
                id="window-type"
                v-model="formStore.data.windowType"
                :options="windowTypes"
                :placeholder="$t('step_heating_details.placeholder_window_type')"
                @change="$emit('updateHeatingLoad')"
              />
            </FormElement>
          </div>
        </div>
      </div>

      <div class="form__subsection">
        <h3 class="form__subtitle">
          {{ $t('step_heating_details.subtitle_living_area_information') }}
        </h3>

        <div class="row form__row has--large-space">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.heatingIndoorTemperature.formValidationRule"
              :error="validator.indoorTempHeating?.error"
              :required="projectStore.formPropertyIsRequired('heatingIndoorTemperature')"
              label-key="step_heating_details.label_indoor_temperature"
            >
              <ComboBox
                id="indoor-temp-heating"
                v-model="formStore.data.heatingIndoorTemperature"
                :options="heatingIndoorTemperatures"
                :placeholder="$t('step_heating_details.placeholder_indoor_temperature')"
                @change="$emit('updateHeatingLoad')"
              />
            </FormElement>
          </div>

          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.heatingVentilationType.formValidationRule"
              :error="validator.heatingVentilationType?.error"
              :required="projectStore.formPropertyIsRequired('heatingVentilationType')"
              label-key="step_heating_details.label_type_of_ventilation"
            >
              <ComboBox
                id="heating-ventilation-type"
                v-model="formStore.data.heatingVentilationType"
                :options="ventilationTypes"
                :placeholder="$t('step_heating_details.placeholder_type_of_ventilation')"
                @change="$emit('updateHeatingLoad')"
              />
            </FormElement>
          </div>
        </div>
      </div>

      <CalculationResultBadge
        id="heating-calc-badge"
        :badge-label="$t('step_heating_details.badge_calculated_heat_loss_load')"
        :calculated-load="calculatedHeatingLoad"
        icon-class="ic-ground"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.section {
  &--heat-loss-calculation {
    .form__section-inner {
      padding-bottom: rem-calc(150px);
    }
  }
}
</style>
